"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connectWithYouTube } from "@/app/api/social-connect";
import { useTranslation } from "react-i18next";
import { useYouTubeIndex } from "./useYouTubeIndex";
import { sessionKeys } from "@/lib/sessionKeys";

export interface YouTubeAccount {
    id: string;
    username?: string;
    name?: string;
    profile_picture_url?: string;
    connectionId: string;
    connectionType: "youtube";
}

export interface UseYouTubeConnectionsReturn {
    isLoading: boolean;
    isConnecting: boolean;
    accounts: YouTubeAccount[];
    error: string | null;
    connectYouTube: (redirectTo?: string) => Promise<void>;
    disconnectYouTube: (connectionId: string) => Promise<void>;
    loadAccounts: () => Promise<YouTubeAccount[]>;
    indexingMessages: string[];
    isIndexing: Record<string, boolean>;
    currentMessageIndex: Record<string, number>;
    triggerIndex: (accountId: string, connectionId: string, skipPost?: boolean) => Promise<void>;
}

const SOCIAL_ACCOUNTS_ENDPOINT = "/api/social/accounts?types=youtube";
const YOUTUBE_STATUS_ENDPOINT = "/api/youtube/social-fetch-status";
const YOUTUBE_DISCONNECT_ENDPOINT = "/api/youtube/disconnect";

const ACTIVE_STATUSES = new Set(["in_progress", "processing", "running"]);

export function useYouTubeConnections(): UseYouTubeConnectionsReturn {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    const [accounts, setAccounts] = useState<YouTubeAccount[]>([]);
    const [error, setError] = useState<string | null>(null);

    const oauthHandledRef = useRef<boolean>(false);
    const previousAccountIds = useRef<Set<string>>(new Set());
    const initialAutoIndexDoneRef = useRef<boolean>(false);

    const {
        isIndexing,
        currentMessageIndex,
        indexingMessages,
        triggerIndex,
    } = useYouTubeIndex();

    const loadAccounts = useCallback(async (): Promise<YouTubeAccount[]> => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await fetch(SOCIAL_ACCOUNTS_ENDPOINT, { cache: "no-store" });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.error || "Failed to load YouTube accounts");
            const youtube = (data?.accounts || []).filter(
                (a: { connectionType: string }) => a.connectionType === "youtube"
            );
            setAccounts(youtube);
            return youtube;
        } catch (e: any) {
            setError(e?.message || "Failed to load YouTube accounts");
            setAccounts([]);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, []);

    const checkAutoIndexingStatus = useCallback(async () => {
        if (accounts.length === 0) return;

        for (const account of accounts) {
            if (isIndexing[account.id]) continue;

            const sessionKey = sessionKeys.youtubeIndexed(account.id);
            if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(sessionKey) === "true") {
                console.log("[AUTO-INDEX:YT] already auto-indexed this session, skipping", { accountId: account.id });
                continue;
            }

            try {
                const qs = new URLSearchParams({
                    connectionId: account.connectionId,
                });
                const resp = await fetch(`${YOUTUBE_STATUS_ENDPOINT}?${qs.toString()}`, {
                    cache: "no-store",
                });
                const json = await resp.json();
                const status = (json?.status || "").toString().toLowerCase();

                console.log("[AUTO-INDEX:YT] status check", { accountId: account.id, status });

                if (ACTIVE_STATUSES.has(status)) {
                    console.log("[AUTO-INDEX:YT] triggering auto-index (resume active)", { accountId: account.id, status });
                    try { sessionStorage.setItem(sessionKey, "true"); } catch {}
                    triggerIndex(account.id, account.connectionId, true);
                }
            } catch {
                // Ignore errors
            }
        }
    }, [accounts, isIndexing, triggerIndex]);

    useEffect(() => {
        if (accounts.length === 0) return;

        const pollInterval = setInterval(checkAutoIndexingStatus, 5000);
        return () => clearInterval(pollInterval);
    }, [accounts.length, checkAutoIndexingStatus]);

    useEffect(() => {
        loadAccounts();
    }, [loadAccounts]);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const searchParams = new URLSearchParams(window.location.search || "");
        const youtubeOauthStatus = searchParams.get("youtube_oauth");

        const wasRedirectedFromYouTube =
            sessionStorage.getItem(sessionKeys.youtubeConnecting) === "true" ||
            youtubeOauthStatus === "success";

        if (wasRedirectedFromYouTube && !oauthHandledRef.current) {
            oauthHandledRef.current = true;
            initialAutoIndexDoneRef.current = true; // OAuth handler owns the trigger
            try {
                sessionStorage.removeItem(sessionKeys.youtubeConnecting);
            } catch {}

            loadAccounts().then((loaded) => {
                if (loaded && loaded.length > 0) {
                    const first = loaded[0];
                    console.log("[AUTO-INDEX:YT] triggering auto-index (post-OAuth)", { accountId: first.id });
                    try { sessionStorage.setItem(sessionKeys.youtubeIndexed(first.id), "true"); } catch {}
                    triggerIndex(first.id, first.connectionId);
                }
            });
        }
    }, [loadAccounts, triggerIndex]);

    const connectYouTube = useCallback(async (redirectTo?: string) => {
        try {
            setIsConnecting(true);
            sessionStorage.setItem(sessionKeys.youtubeConnecting, "true");
            await connectWithYouTube(redirectTo ?? "/account");
        } finally {
            setIsConnecting(false);
        }
    }, []);

    const disconnectYouTube = useCallback(
        async (connectionId: string) => {
            try {
                const res = await fetch(YOUTUBE_DISCONNECT_ENDPOINT, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ connectionId }),
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    throw new Error((data as any)?.error || "Failed to disconnect YouTube account");
                }
                await loadAccounts();
            } catch (e: any) {
                setError(e?.message || "Failed to disconnect YouTube account");
            }
        },
        [loadAccounts]
    );

    useEffect(() => {
        if (accounts.length > 0) {
            const currentAccountIds = new Set(accounts.map((account) => account.id));
            const previous = previousAccountIds.current;

            if (previous.size > 0) {
                const newIds = Array.from(currentAccountIds).filter((id) => !previous.has(id));
                if (newIds.length > 0) {
                    const newAccounts = accounts.filter((account) => newIds.includes(account.id));
                    newAccounts.forEach((account) => {
                        triggerIndex(account.id, account.connectionId);
                    });
                }
            }

            previousAccountIds.current = currentAccountIds;
        }
    }, [accounts, triggerIndex]);

    // Auto-index once per new browser session when accounts first load
    useEffect(() => {
        if (initialAutoIndexDoneRef.current) return;
        if (accounts.length === 0) return;

        initialAutoIndexDoneRef.current = true;

        for (const account of accounts) {
            const sessionKey = sessionKeys.youtubeIndexed(account.id);
            try {
                if (sessionStorage.getItem(sessionKey) === "true") {
                    console.log("[AUTO-INDEX:YT] already auto-indexed this session, skipping", { accountId: account.id });
                    continue;
                }
            } catch {}
            if (isIndexing[account.id]) {
                console.log("[AUTO-INDEX:YT] already indexing, skipping", { accountId: account.id });
                continue;
            }
            console.log("[AUTO-INDEX:YT] triggering auto-index (new session, first load)", { accountId: account.id });
            try { sessionStorage.setItem(sessionKey, "true"); } catch {}
            triggerIndex(account.id, account.connectionId);
        }
    }, [accounts, isIndexing, triggerIndex]);

    return useMemo(
        () => ({
            isLoading,
            isConnecting,
            accounts,
            error,
            connectYouTube,
            disconnectYouTube,
            loadAccounts,
            indexingMessages,
            isIndexing,
            currentMessageIndex,
            triggerIndex,
        }),
        [
            isLoading,
            isConnecting,
            accounts,
            error,
            connectYouTube,
            disconnectYouTube,
            loadAccounts,
            indexingMessages,
            isIndexing,
            currentMessageIndex,
            triggerIndex,
        ]
    );
}
